import {useState} from 'react';
import {
  EntityOccurrenceType,
  EntityOccurrenceTypeCategory,
} from '../../../../../../backend/responses/entity-occurrence';
import {Box, Typography} from '@mui/material';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {CardCollapse} from '../../../../../../components/shared/card-collapse';
import './occurrences-types.professional-caregiver.screen';
import {Role} from '../../../../../../backend/interfaces/role';
import {ComboActions} from '../../../../../../components/combo-actions/combo-actions';
import {FiTrash2, FiEdit} from 'react-icons/fi';
import './occurrences-types.scss';
import {useAuth} from '../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {GrRevert} from 'react-icons/gr';

type OccurrencesTypesProps = {
  roles: {[id: string]: Role};
  occurrencesData: EntityOccurrenceType[];
  highlightOccurrenceTypeId?: string;
  handleEdit: (id: string, category: EntityOccurrenceTypeCategory) => void;
  handleDelete: (id: string, name: string, entityId: string | undefined) => void;
};

export default function OccurrencesTypes({
  roles,
  occurrencesData,
  highlightOccurrenceTypeId,
  handleEdit,
  handleDelete,
}: OccurrencesTypesProps) {
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();
  const [isUsersExpanded, setIsUsersExpanded] = useState(true);
  const [isGeneralExpanded, setIsGeneralExpanded] = useState(true);

  const renderOccurrencesTypesContainer = (containerType: EntityOccurrenceTypeCategory) => {
    const categoryTypes = occurrencesData.filter(
      (type) => type.occurrenceType.category === containerType
    );

    categoryTypes.sort((a, b) => {
      if (a.occurrenceType.entityId && !b.occurrenceType.entityId) return 1;
      else if (!a.occurrenceType.entityId && b.occurrenceType.entityId) return -1;
      else return 0;
    });

    return (
      <Box className="occurrences-types-container">
        <Box className="occurrences-types-header">
          <Typography className="occurrences-type-name">
            {t(translations.office.professional_caregiver.settings.occurrences.occurrence)}
          </Typography>
          <Typography className="occurrences-type-active">
            {t(translations.office.professional_caregiver.settings.occurrences.active)}
          </Typography>
          <Typography className="occurrences-type-action">
            {t(translations.office.professional_caregiver.settings.occurrences.action_required)}
          </Typography>
          <Typography className="occurrences-type-notify">
            {t(translations.office.professional_caregiver.settings.occurrences.notify)}
          </Typography>
        </Box>
        {categoryTypes.length === 0 && (
          <Typography className="occurrences-empty">
            {containerType === 'general'
              ? t(translations.office.professional_caregiver.settings.occurrences.empty_general)
              : t(translations.office.professional_caregiver.settings.occurrences.empty_patients)}
          </Typography>
        )}
        {categoryTypes.length > 0 &&
          categoryTypes.map(({occurrenceType, entityOccurrenceType, summary}) => {
            const color = occurrenceType.entityId ? 'var(---dcm-gray)' : 'var(--dcm-gray-input)';
            const notifyRolesText =
              summary?.notifyRolesIds && summary.notifyRolesIds.length > 0
                ? summary.notifyRolesIds
                    .map((notify) => roles[notify]?.name)
                    .filter(Boolean)
                    .join(', ')
                : '-';

            return (
              <Box
                className="occurrences-type"
                key={occurrenceType.id}
                sx={{
                  backgroundColor:
                    highlightOccurrenceTypeId === occurrenceType.id
                      ? 'var(--dcm-blue-hover)'
                      : 'transparent',
                }}
              >
                <Box className="occurrences-type-name">
                  <Typography
                    order={occurrenceType.displayOrder}
                    title={occurrenceType.name}
                    sx={{
                      color,
                    }}
                  >
                    {occurrenceType.name}
                  </Typography>
                </Box>
                <Box className="occurrences-type-active">
                  <Typography
                    sx={{
                      color,
                    }}
                  >
                    {summary?.isActive
                      ? t(translations.device_health.yes_label)
                      : t(translations.device_health.no_label)}
                  </Typography>
                </Box>
                <Box className="occurrences-type-action">
                  <Typography
                    sx={{
                      color,
                    }}
                  >
                    {summary?.actionRequired
                      ? t(translations.device_health.yes_label)
                      : t(translations.device_health.no_label)}
                  </Typography>
                </Box>
                <Box className="occurrences-type-notify">
                  <Typography
                    title={notifyRolesText !== '-' ? notifyRolesText : undefined}
                    sx={{
                      color,
                    }}
                  >
                    {notifyRolesText}
                  </Typography>
                </Box>
                <Box className="occurrences-type-menu">
                  <ComboActions
                    {...{
                      actions: [
                        {
                          label: t(
                            translations.office.professional_caregiver.settings.templates.edit
                          ),
                          icon: {element: FiEdit},
                          callback: () => handleEdit(occurrenceType.id, containerType),
                          disabled: !hasWritePermission(RolePermissionType.Settings_Occurrences),
                          disabledTooltip: t(
                            translations.office.professional_caregiver.settings.occurrences
                              .no_permissions
                          ),
                        },
                        {
                          label: occurrenceType.entityId
                            ? t(
                                translations.office.professional_caregiver.settings.templates.delete
                              )
                            : t(
                                translations.office.professional_caregiver.settings.templates.revert
                              ),
                          icon: {element: occurrenceType.entityId ? FiTrash2 : GrRevert},
                          callback: () =>
                            handleDelete(
                              occurrenceType.id,
                              occurrenceType.name,
                              occurrenceType.entityId
                            ),
                          disabled:
                            !hasWritePermission(RolePermissionType.Settings_Occurrences) ||
                            (!occurrenceType.entityId && !entityOccurrenceType),
                          disabledTooltip: !occurrenceType.entityId
                            ? t(
                                translations.office.professional_caregiver.settings.occurrences
                                  .delete_default
                              )
                            : !hasWritePermission(RolePermissionType.Settings_Occurrences)
                              ? t(
                                  translations.office.professional_caregiver.settings.occurrences
                                    .no_permissions
                                )
                              : '',
                        },
                      ],
                      wrapperSX: {
                        color: 'var(--dcm-gray-light)',
                      },
                      triggerSX: {width: '28px', height: '28px'},
                    }}
                  />
                </Box>
              </Box>
            );
          })}
      </Box>
    );
  };

  return (
    <>
      <CardCollapse
        defaultExpand={isUsersExpanded}
        cardProps={{className: 'occurrences-card-collapse'}}
        onChange={(expanded) => setIsUsersExpanded(expanded)}
        collapsableHeader={
          <Typography className="occurrences-card-collapse-header" noWrap>
            {t(translations.office.professional_caregiver.settings.occurrences.patients)}
          </Typography>
        }
      >
        {renderOccurrencesTypesContainer('patients')}
      </CardCollapse>
      <CardCollapse
        defaultExpand={isGeneralExpanded}
        onChange={(expanded) => setIsGeneralExpanded(expanded)}
        cardProps={{className: 'occurrences-card-collapse'}}
        collapsableHeader={
          <Typography className="occurrences-card-collapse-header" noWrap>
            {t(translations.office.professional_caregiver.settings.occurrences.general_plural)}
          </Typography>
        }
      >
        {renderOccurrencesTypesContainer('general')}
      </CardCollapse>
    </>
  );
}
